import {
    Box,
    Stack,
    Button,
    Typography,
    IconButton,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { HttpError, useCustom, useSelect, useTranslate, useNotification, useList } from "@refinedev/core";
import { CreateOrEdit } from "@/components/editcreate";
import { TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { companyFormSchema, CompanyFormSchema } from "@/utils/zod/companyFormValidation";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useFieldArray } from "react-hook-form";
import { RHFAutocomplete } from "@/components/form/RHFAutocomplete";
import RHFAutoCompleteString from "./RHFAutocompleteString";
import { cityStatesData } from "@/utils/data-provider/data";
import { IContact } from "@/utils/zod/contactFormValidation";

interface Props {
    actionType: "create" | "edit";
    title?: string;
    onSave?: (ledgerName: string) => void;
    defaultFormValues?: any;
    redirect?: boolean;
}

export const CompanyAddEditForm = ({ actionType, title, onSave, defaultFormValues, redirect = true }: Props) => {
    const { open } = useNotification();

    const t = useTranslate();
    const formValues = useForm<CompanyFormSchema, HttpError>({
        mode: "onSubmit",
        refineCoreProps: {
            resource: "companies",
            redirect: redirect ? "list" : false,
            successNotification: () => {
                return false;
            },
            onMutationSuccess: (data: any) => {
                if (actionType === "create") {
                    data.data.forEach((company: any) => {
                        if (company.error) {
                            open?.({
                                message: company.error,
                                type: "error",
                            })
                        } else {
                            open?.({
                                message: `Company ${company.ledger_name} created successfully`,
                                type: "success",
                            });
                            onSave?.(company.ledger_name);
                        }
                    });
                } else {
                    if (data.data.error) {
                        open?.({
                            message: data.data.error,
                            type: "error",
                        });
                    } else {
                        open?.({
                            message: `Company ${data.data.ledger_name} updated successfully`,
                            type: "success",
                        });
                    }
                }
            },
            ...(actionType === "edit" && {
                queryOptions: {
                    select: (data: any) => {
                        const members = data.data.accounts?.reduce((acc: number[], member: { id: number; name: string; user: { name: string; phone_number: number } }) => {
                            acc.push(member.id);
                            return acc;
                        }, []);
                        
                        // Extract IDs from send_outstanding_to objects
                        const send_outstanding_to_accounts = data.data.send_outstanding_to_accounts?.map(
                            (item: any) => item.id?.toString()
                        ) || [];
                        
                        const transformedData = ({
                            ...data.data,
                            addresses: data.data.addresses?.map((address: any) => ({
                                ...address,
                                address: address.line1 || "",
                            })),
                            send_outstanding_to_accounts: send_outstanding_to_accounts,
                            members: members,
                        });
                        return { data: transformedData };
                    }
                }
            }),
        },
        resolver: zodResolver(companyFormSchema),
        defaultValues: defaultFormValues ?? {
            ledger_name: "",
            legal_name: "",
            pan_number: null,
            email: "",
            notes: "",
            members: [],
            gstin: null,
            tags: [],
            category: "retailer",
            type: "customer",
            send_outstanding_to_accounts: [],
            addresses: [{
                branch_name: "",
                city: "",
                country: "India",
                email: "",
                gstin: null,
                landline_number: "",
                address: "",
                pincode: "",
                state: "",
                transport_name: "",
                transport_delivery_station: "",
            }],
        },
    });

    const { control, handleSubmit, refineCore: { onFinish } } = formValues;
    const { fields, append, remove } = useFieldArray({
        control,
        name: "addresses",
    });

    const onSubmit = handleSubmit((values, event) => {
        // Prevent event from bubbling up to parent form
        event?.preventDefault();
        event?.stopPropagation();
        
        const { members, ...restValues } = values; // Destructure to exclude `members`

        const finishValues = {
            ...restValues,
            ledger_name: values.ledger_name?.trim().toUpperCase(),
            pan_number: values.pan_number?.trim() ? values.pan_number.trim().toUpperCase() : null,
            gstin: values.gstin?.trim() ? values.gstin.trim().toUpperCase() : null,
            addresses: values.addresses?.map((address: any) => ({
                ...address,
                line1: address.address || "",
            })),
            // Convert string IDs to contact objects for API submission
            send_outstanding_to_accounts: values.send_outstanding_to_accounts?.map((id: string) => ({ id })),
            accounts: values.members?.map((id: string) => ({ id }))
        };
        onFinish(actionType === "create" ? [finishValues] : finishValues);
    });

    const { data: tags } = useCustom<string[]>({
        url: "/contact-tags/static",
        method: "get",
    })

    const { options: membersOptions } = useSelect({
        resource: "accounts",
        optionLabel: (record: any) => `${(record.name && record.user?.name) ? `${record.name} (${record.user?.name})` : record.name ? record.name : record.user?.name ? `(${record.user?.name})` : ""}: ${record.user?.phone_number || ""}`,
        optionValue: (record: any) => record.id,
    });

    const { data: accounts } = useList({
        resource: "accounts"
    })

    const { data: agents } = useCustom<IContact[]>({
        url: "/accounts/companies/agent",
        method: "get",
    });
    const memberIds = formValues.watch("members") ?? [];
    const members = accounts?.data?.filter((account: any) => memberIds.includes(account.id)) ?? []; 

    const { data: { data: { transport_names: transportNames, delivery_stations: deliveryStations } = {} } = {} } = useCustom<{ transport_names: string[], delivery_stations: string[] }>({
        url: "/companies/addresses/transport-names-and-delivery-stations",
        method: "get"
    });

    return (
        <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit} title={{ edit: `Edit Company #${formValues.getValues("ledger_name")}`, create: title ?? "Add Company" }}>
            <Box sx={{ p: 3 }}>
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} autoComplete="off">
                    {/* Company Basic Details */}
                    <Stack direction="row" spacing={2}>
                        <TextFieldElement
                            name="ledger_name"
                            label={t("company.fields.ledger_name")}
                            fullWidth
                            required
                            size="small"
                            slotProps={{
                                htmlInput: {
                                    sx: {
                                        textTransform: "uppercase",
                                    },
                                },
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main',
                                            fontSize: '24px',
                                        },
                                    },
                                }
                            }}
                            onBlur={(e) => {
                                formValues.setValue("legal_name", e.target.value.toUpperCase());
                            }}
                            autoComplete="new-name"
                        />

                        <TextFieldElement
                            name="legal_name"
                            label={t("company.fields.legal_name")}
                            fullWidth
                            size="small"
                            autoComplete="new-name"
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextFieldElement
                            name="gstin"
                            label={t("company.fields.gstin")}
                            fullWidth
                            size="small"
                            onBlur={(e) => {
                                const value = e.target.value.toUpperCase();
                                formValues.setValue("gstin", value);
                                if (value.length === 15) {
                                    formValues.setValue("pan_number", value.slice(2).slice(0, 10));
                                }
                            }}
                            onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                e.target.value = value;
                            }}
                            slotProps={{
                                htmlInput: {
                                    sx: {
                                        textTransform: "uppercase",
                                    },
                                },
                            }}
                            autoComplete="gstin"
                        />

                        <TextFieldElement
                            name="pan_number"
                            label={t("company.fields.pan_number")}
                            fullWidth
                            size="small"
                            slotProps={{
                                htmlInput: {
                                    sx: {
                                        textTransform: "uppercase",
                                    },
                                },
                            }}
                            onBlur={(e) => {
                                const value = e.target.value.toUpperCase();
                                formValues.setValue("pan_number", value);
                            }}
                            onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                e.target.value = value;
                            }}
                            autoComplete="pan"
                        />
                    </Stack>

                    <RHFAutocomplete
                        name="members"
                        label={t("company.fields.members")}
                        options={membersOptions}
                        multiple={true}
                        size="small"
                    />

                    <Stack direction="row" spacing={2}>
                        <RHFAutoCompleteString
                            name="category"
                            label={t("company.fields.category")}
                            options={["retailer", "wholesaler"]}
                            multiple={false}
                            createNew
                            sx={{ flex: 1 }}
                            required
                            size="small"
                        />

                        <RHFAutoCompleteString
                            name="type"
                            label={t("company.fields.type")}
                            options={["customer", "agent"]}
                            multiple={false}
                            createNew
                            sx={{ flex: 1 }}
                            required
                            size="small"
                        />

                        <TextFieldElement
                            name="email"
                            label={t("company.fields.email")}
                            type="email"
                            fullWidth
                            sx={{ flex: 1 }}
                            size="small"
                            autoComplete="new-email"
                        />
                    </Stack>

                    <RHFAutoCompleteString
                        name="tags"
                        label={t("company.fields.tags")}
                        options={tags?.data ?? []}
                        multiple={true}
                        createNew
                        size="small"
                        sx={{ flex: 1 }}
                    />

                    <Box>
                        <Box component="h3">{t("company.fields.send_outstanding_to")}</Box>
                        <RHFAutocomplete
                            name="send_outstanding_to_accounts"
                            label={t("company.fields.send_outstanding_to")}
                            options={[{
                                title: "Agents",
                                options: (agents?.data || []).map((agent: any) => ({
                                    label: `${(agent.name && agent.user?.name) ? `${agent.name} (${agent.user?.name})` : agent.name ? agent.name : agent.user?.name ? `(${agent.user?.name})` : ""}: ${agent.user?.phone_number || ""}`,
                                    value: agent.id,
                                })),
                            },{
                                title: "Members",
                                options: members.map((member: any) => ({
                                    label: `${(member.name && member.user?.name) ? `${member.name} (${member.user?.name})` : member.name ? member.name : member.user?.name ? `(${member.user?.name})` : ""}: ${member.user?.phone_number || ""}`,
                                    value: member.id,
                                })),
                            }]}
                            multiple={true}
                            size="small"
                            createNew={false}
                            sx={{ flex: 1 }}
                            grouped={true}
                        />
                    </Box>

                    {/* Addresses Section */}
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" >
                            <Box component="h3">{t("company.fields.addresses")}</Box>
                            <Button variant="text" sx={{ fontWeight: 500, '&:hover': { fontWeight: 600 } }} onClick={() => append({ branch_name: "", gstin: "", line1: "", line2: "", line3: "", city: "", state: "", country: "", pincode: "", email: "", landline_number: "" })}>
                                +{t("company.buttons.add_address")}
                            </Button>
                        </Stack>
                        {fields.map((_, index) => {
                            formValues.setValue(`addresses.${index}.branch_name`, `Shop ${index + 1}`);
                            return (
                                <Stack spacing={2} key={index} border="1px solid" borderColor="divider" borderRadius={1} p={2} mb={2}>
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Typography variant="subtitle1">
                                            Address {index + 1}:
                                        </Typography>
                                        <IconButton color="error" onClick={() => remove(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextFieldElement
                                            name={`addresses.${index}.branch_name`}
                                            label={t("company.fields.branch_name")}
                                            autoComplete="branch-name"
                                            size="small"
                                            sx={{ flex: 0.5 }}
                                        />

                                        <TextFieldElement
                                            name={`addresses.${index}.pincode`}
                                            label={t("company.fields.pincode")}
                                            size="small"
                                            sx={{ flex: 0.5 }}
                                            onBlur={(e) => {
                                                let pincode = Number(e.target.value);
                                                if (pincode >= 100000 && pincode <= 999999) {
                                                    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
                                                        .then((pinDeets) => pinDeets.json())
                                                        .then((pinDeets) => {
                                                            if (pinDeets && pinDeets[0]) {
                                                                pinDeets = pinDeets[0].PostOffice[0];
                                                                formValues.setValue(`addresses.${index}.state`, pinDeets.State);
                                                                formValues.setValue(`addresses.${index}.city`, pinDeets.Division);
                                                            }
                                                        });
                                                }
                                            }}
                                            autoComplete="pincode"
                                        />
                                        <TextFieldElement
                                            name={`addresses.${index}.address`}
                                            label={t("company.fields.address")}
                                            sx={{ flex: 1 }}
                                            autoComplete="new-address"
                                            size="small"
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={2}>
                                        <RHFAutoCompleteString
                                            name={`addresses.${index}.state`}
                                            label={t("company.fields.state")}
                                            options={Object.keys(cityStatesData)}
                                            multiple={false}
                                            createNew
                                            sx={{ flex: 1 }}
                                            size="small"
                                        />
                                        <RHFAutoCompleteString
                                            name={`addresses.${index}.city`}
                                            label={t("company.fields.city")}
                                            sx={{ flex: 1 }}
                                            options={cityStatesData[formValues.watch(`addresses.${index}.state`) as keyof typeof cityStatesData] ?? []}
                                            multiple={false}
                                            createNew
                                            size="small"
                                        />
                                        <TextFieldElement
                                            name={`addresses.${index}.country`}
                                            label={t("company.fields.country")}
                                            sx={{ flex: 1 }}
                                            autoComplete="new-country"
                                            size="small"
                                        />
                                    </Stack>
                                    <Stack direction="row" spacing={2}>

                                        <TextFieldElement
                                            name={`addresses.${index}.gstin`}
                                            label={t("company.fields.gstin")}
                                            fullWidth
                                            autoComplete="gstin"
                                            size="small"
                                            onBlur={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                formValues.setValue(`addresses.${index}.gstin`, value);
                                            }}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                e.target.value = value;
                                            }}
                                            slotProps={{
                                                htmlInput: {
                                                    sx: {
                                                        textTransform: "uppercase",
                                                    },
                                                },
                                            }}
                                        />
                                        <TextFieldElement
                                            name={`addresses.${index}.email`}
                                            label={t("company.fields.email")}
                                            type="email"
                                            fullWidth
                                            autoComplete="new-email"
                                            size="small"
                                        />

                                        <TextFieldElement
                                            name={`addresses.${index}.landline_number`}
                                            label={t("company.fields.landline_number")}
                                            fullWidth
                                            autoComplete="landline-number"
                                            size="small"
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={2}>
                                        <RHFAutoCompleteString
                                            name={`addresses.${index}.transport_name`}
                                            label={t("company.fields.transport_name")}
                                            options={transportNames ?? []}
                                            multiple={false}
                                            createNew
                                            sx={{ flex: 1 }}
                                            size="small"
                                        />
                                        <RHFAutoCompleteString
                                            name={`addresses.${index}.transport_delivery_station`}
                                            label={t("company.fields.transport_delivery_station")}
                                            options={deliveryStations ?? []}
                                            multiple={false}
                                            createNew
                                            sx={{ flex: 1 }}
                                            size="small"
                                        />
                                    </Stack>
                                </Stack>
                            )
                        })}
                    </Box>
                </Box>
            </Box>
        </CreateOrEdit>
    );
};
